import React, { useState } from 'react';
import { ViewerComponentProps } from '../types';
import { cn } from '@/common/utils';
import { Loader2 } from 'lucide-react';

/**
 * Image Viewer component for JPG, PNG, WEBP
 * Simple implementation with loading state
 */
export const ImageViewer: React.FC<ViewerComponentProps> = ({
  fileUrl,
  fileName,
  className,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  return (
    <div className={cn('flex flex-col w-full h-full bg-gray-50', className)}>
      {fileName && (
        <div className="px-4 py-2 border-b bg-white">
          <h3 className="font-semibold text-sm truncate" title={fileName}>
            {fileName}
          </h3>
        </div>
      )}

      <div className="flex-1 overflow-auto flex items-center justify-center p-4 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        )}

        {error ? (
          <div className="text-center text-gray-500">
            <p className="text-sm">Failed to load image</p>
            <p className="text-xs mt-1 text-gray-400 break-all px-4">{fileUrl}</p>
          </div>
        ) : (
          <img
            src={fileUrl}
            alt={fileName || 'Preview'}
            className={cn(
              'max-w-full max-h-full object-contain',
              loading && 'opacity-0'
            )}
            onLoad={handleLoad}
            onError={handleError}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Matcher function for registry
 * Returns true if the file is an image (JPG, JPEG, PNG, WEBP)
 */
export const imageMatcher = (url: string, fileType: string | null): boolean => {
  if (fileType === 'image') return true;

  const lowerUrl = url.toLowerCase();
  return (
    lowerUrl.endsWith('.jpg') ||
    lowerUrl.endsWith('.jpeg') ||
    lowerUrl.endsWith('.png') ||
    lowerUrl.endsWith('.webp')
  );
};
